/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, { IresponseData, IresponseDataPaginated } from "@/lib/api";
import { EPaymentMethod, EPaymentStatus } from "./payment";
import { EfromClient } from "./client";
import { Combo } from "./combo";
import { ESupplierStatus } from "./supplier";

// Enums
export enum ESubscriptionType {
  SLOT = "SLOT",
  FULL_ACCOUNT = "FULL_ACCOUNT",
}

export enum ESubscriptionStatus {
  ACTIVE = "ACTIVE",
  EXPIRING = "EXPIRING",
  EXPIRED = "EXPIRED",
  CANCELLED = "CANCELLED",
}

export enum ESubType {
  SINGLE = "SINGLE",
  COMBO = "COMBO",
}

// Interfaces
export interface StreamingAccountInfo {
  email: string;
  service: string;
}

export interface SubscriptionItemInfo {
  id: number;
  serviceId: number;
  streamingAccountId: number;
  subscriptionType: ESubscriptionType;
  slotId?: number | null;
  pricePlanId: number;
  slot?: { id: number; name: string; status: string };
  service?: { id: number; name: string; icon: string; color?: string };
  streamingAccount?: {
    id: number;
    email: string;
    password?: string;
    status: string;
    accountType: string;
  };
  pricePlan?: {
    id: number;
    name: string;
    price: number;
    duration: number;
    pricePlanType: string;
  };
}

export interface SubscriptionPaymentInfo {
  id: string;
  subscriptionId: string;
  clientId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: EPaymentMethod;
  status: EPaymentStatus;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface SubscriptionClientInfo {
  id: string;
  name: string;
  phone: string;
  email?: string;
  status: string;
  activeServicesCount: number;
  monthlyValue: number | string;
  notes?: string;
}

export interface SubscriptionSupplierInfo {
  id: number;
  name: string;
  phone?: string | null;
  email?: string | null;
  score?: number | null;
  status?: ESupplierStatus;
  notes?: string;
  createdAt?: string;
}

export interface Subscription {
  id: string;
  clientId: string;
  supplierId?: number;
  ownerType?: EownerType;
  type: ESubType;
  comboId?: number;
  startDate: string;
  endDate: string;
  price: number;
  status: ESubscriptionStatus;
  daysRemaining: number;
  createdAt: string;
  updatedAt?: string;
  subscriptionItems: SubscriptionItemInfo[];
  client?: SubscriptionClientInfo; // ownerType = CLIENT
  supplier?: SubscriptionSupplierInfo; // ownerType = SUPPLIER
  payments: SubscriptionPaymentInfo[];
  combo: Combo;
}

/**
 * Devuelve el serviceId efectivo:
 * - SINGLE: toma el serviceId del primer subscriptionItem
 * - COMBO: no hay un único servicio, devuelve undefined
 */
export function getSubscriptionServiceId(
  subscription: Subscription,
): number | undefined {
  if (subscription.type === ESubType.SINGLE) {
    return subscription.subscriptionItems?.[0]?.serviceId;
  }
  return undefined;
}

/**
 * Devuelve el StreamingAccountInfo efectivo:
 * - SINGLE: toma el streamingAccount del primer subscriptionItem
 * - COMBO: no hay una única cuenta, devuelve undefined
 */
export function getSubscriptionStreamingAccount(subscription: Subscription):
  | {
      id: number;
      email: string;
      status: string;
      accountType: string;
    }
  | undefined {
  if (subscription.type === ESubType.SINGLE) {
    return subscription.subscriptionItems?.[0]?.streamingAccount;
  }
  return undefined;
}

// Request DTOs
export interface SubscriptionItemRequest {
  serviceId: number;
  streamingAccountId: number;
  subscriptionType: ESubscriptionType;
  pricePlanId: number;
  slotId?: number;
}

export interface CreateSubscriptionRequest {
  clientId?: number;
  supplierId?: number;
  ownerType?: EownerType;
  newClient?: {
    name: string;
    phone: string;
    from: EfromClient;
  };
  type: ESubType;
  startDate: string;
  paymentMethod: EPaymentMethod;
  paymentStatus: EPaymentStatus;
  subscriptionItems: SubscriptionItemRequest[];
  comboId?: number;
  isNotify?: boolean;
}

export interface UpdateSubscriptionRequest {
  startDate?: string;
  endDate?: string;
  price?: number;
  status?: ESubscriptionStatus;
}

export interface ExtendSubscriptionRequest {
  paymentStatus: EPaymentStatus;
  paymentMethod: EPaymentMethod;
}

export interface TransferSubscriptionRequest {
  targetStreamingAccountId: number;
  targetSlotId: number;
  isNotify: boolean;
}

export enum EownerType {
  CLIENT = "CLIENT",
  SUPPLIER = "SUPPLIER",
}

// Filter params
export interface SubscriptionFilters {
  clientId?: number;
  serviceId?: number;
  streamingAccountId?: number;
  subscriptionType?: ESubscriptionType;
  type?: ESubType;
  status?: ESubscriptionStatus;
  ownerType?: EownerType;
  supplierId?: number;
  page?: number;
  limit?: number;
}

/**
 * Elimina campos vacíos de un objeto
 */
function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

/**
 * Servicio para gestionar suscripciones
 */
export const subscriptionService = {
  /**
   * Registrar una nueva suscripción
   */
  async create(
    data: CreateSubscriptionRequest,
  ): Promise<IresponseData<Subscription>> {
    try {
      const response = await apiClient.post<IresponseData<Subscription>>(
        "/subscriptions",
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar la suscripción. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener todas las suscripciones
   */
  async getAll(
    filters?: SubscriptionFilters,
  ): Promise<IresponseDataPaginated<Subscription[]>> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<
        IresponseDataPaginated<Subscription[]>
      >("/subscriptions", { params: cleanFilters });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener las suscripciones. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener una suscripción por ID
   */
  async getOne(id: string): Promise<IresponseData<Subscription>> {
    try {
      const response = await apiClient.get<IresponseData<Subscription>>(
        `/subscriptions/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener la suscripción. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Actualizar una suscripción
   */
  async update(
    id: string,
    data: UpdateSubscriptionRequest,
  ): Promise<IresponseData<Subscription>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.put<IresponseData<Subscription>>(
        `/subscriptions/${id}`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar la suscripción. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener suscripciones de un cliente específico
   */
  async getByClient(clientId: string): Promise<IresponseData<Subscription[]>> {
    try {
      const response = await apiClient.get<IresponseData<Subscription[]>>(
        `/subscriptions/client/${clientId}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener las suscripciones del cliente. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Extender una suscripción
   */
  async extend(
    id: string,
    data: ExtendSubscriptionRequest,
  ): Promise<IresponseData<Subscription>> {
    try {
      const response = await apiClient.patch<IresponseData<Subscription>>(
        `/subscriptions/${id}/extend`,
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al extender la suscripción. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Cancelar una suscripción
   */
  async cancel(id: string): Promise<IresponseData<Subscription>> {
    try {
      const response = await apiClient.patch<IresponseData<Subscription>>(
        `/subscriptions/${id}/cancel`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al cancelar la suscripción. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Cambiar el proveedor de una suscripción
   */
  async changeSupplier(
    id: string,
    supplierId: number,
    pricePlanId: number,
  ): Promise<IresponseData<Subscription>> {
    try {
      const response = await apiClient.patch<IresponseData<Subscription>>(
        `/subscriptions/${id}/supplier`,
        { supplierId, pricePlanId },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al cambiar el proveedor. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Transferir una suscripción a otra cuenta/slot
   */
  async transfer(
    id: string,
    data: TransferSubscriptionRequest,
  ): Promise<IresponseData<Subscription>> {
    try {
      const response = await apiClient.patch<IresponseData<Subscription>>(
        `/subscriptions/${id}/transfer`,
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al transferir la suscripción. Por favor, intenta de nuevo.",
      );
    }
  },
};
